import { useEffect, useState } from 'react';

/* Which of the two themes is live, and how to swap it.
   ------------------------------------------------------------------------
   The attribute on <html> is the single source of truth, not React state.
   It has to be, because it is set by an inline script in gatsby-ssr.js before
   the first paint — if React owned the value, the page would render dark and
   then jump to light on hydration, which is the flash the script exists to
   prevent.

   State here only mirrors the attribute so that a control can draw the right
   icon. It starts as 'dark' on both the server and the first client render,
   which is what the SSR'd markup was generated with, and syncs in an effect.
   Anything else is a hydration mismatch.

   The subscriber list is module scope rather than a context. Two controls
   exist — one in the header, one in the mobile sidebar — and although they
   are never visible at the same width, they are both mounted, so a toggle in
   one has to be seen by the other. A store is less machinery than threading a
   provider through Layout for two buttons. */

const STORAGE_KEY = 'theme';
const LIGHT = 'light';
const DARK = 'dark';

const listeners = new Set();

const readTheme = () => {
  if (typeof document === 'undefined') {
    return DARK;
  }
  return document.documentElement.getAttribute('data-theme') === LIGHT ? LIGHT : DARK;
};

/* Dark is the absence of the attribute, not data-theme="dark". That keeps the
   default identical to what the site rendered before themes existed, so a
   visitor who never touches the control cannot be affected by any of this. */
const applyTheme = next => {
  const root = document.documentElement;

  if (next === LIGHT) {
    root.setAttribute('data-theme', LIGHT);
  } else {
    root.removeAttribute('data-theme');
  }

  // Private mode and blocked storage both throw here rather than returning
  // null, and neither is a reason to fail to change the theme.
  try {
    window.localStorage.setItem(STORAGE_KEY, next);
  } catch (e) {
    /* the choice just will not survive a reload */
  }

  listeners.forEach(listener => listener(next));
};

const useTheme = () => {
  const [theme, setTheme] = useState(DARK);

  useEffect(() => {
    setTheme(readTheme());

    listeners.add(setTheme);
    return () => listeners.delete(setTheme);
  }, []);

  const toggleTheme = () => applyTheme(readTheme() === LIGHT ? DARK : LIGHT);

  return [theme, toggleTheme];
};

export default useTheme;
